const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const trCollator = new Intl.Collator("tr-TR", { sensitivity: "base", numeric: true });

const parseDate = (value) => {
  const ms = Date.parse(value || "");
  return Number.isFinite(ms) ? ms : 0;
};

export const normalizeAdminPlatform = (value) => {
  const platform = String(value || "").trim().toLowerCase();
  if (platform.includes("ios") || platform.includes("iphone") || platform.includes("ipad")) return "ios";
  if (platform.includes("android")) return "android";
  if (platform.includes("web") || platform.includes("pwa")) return "web";
  return platform || "unknown";
};

export const adminUserPlatforms = (user = {}) => Array.from(new Set([
  ...(Array.isArray(user.platforms) ? user.platforms : []),
  user.platform,
].map(normalizeAdminPlatform).filter((platform) => platform !== "unknown")));

export const adminUserPlatformLabel = (user = {}) => {
  const platforms = adminUserPlatforms(user);
  const hasIos = platforms.includes("ios");
  const hasAndroid = platforms.includes("android");
  if (hasIos && hasAndroid) return "iOS+Android";
  if (hasIos) return "iOS";
  if (hasAndroid) return "Android";
  if (platforms.includes("web")) return "Web";
  return "—";
};

export const adminUserPushReady = (user = {}) => (
  user.pushReady === true || user.deliveryChannel === "push"
);

export const adminUserIsPremium = (user = {}) => (
  user.isPremium === true || user.membershipPlan === "premium"
);

function versionParts(value) {
  const match = String(value || "").match(/(\d+(?:\.\d+)*)(?:\s*\((\d+)\))?/);
  if (!match) return [];
  return [...match[1].split(".").map(Number), Number(match[2] || 0)];
}

export function compareAdminVersions(left, right) {
  const a = versionParts(left);
  const b = versionParts(right);
  const length = Math.max(a.length, b.length);
  for (let index = 0; index < length; index += 1) {
    const diff = Number(a[index] || 0) - Number(b[index] || 0);
    if (diff !== 0) return diff;
  }
  return trCollator.compare(String(left || ""), String(right || ""));
}

const userName = (user) => `${user?.ad || ""} ${user?.soyad || ""}`.trim() || user?.displayName || "";
const userLanguage = (user) => String(user?.locale || user?.language || "tr").toLowerCase().split(/[-_]/)[0];
const lastActiveMs = (user) => parseDate(user?.lastActiveAt || user?.lastSeenAt);

function matchesActivity(user, filter, now) {
  if (filter === "all") return true;
  const activeAt = lastActiveMs(user);
  if (filter === "never") return activeAt === 0;
  if (!activeAt) return false;
  const age = Math.max(0, now - activeAt);
  if (filter === "live") return age <= 5 * MINUTE;
  if (filter === "24h") return age <= DAY;
  if (filter === "7d") return age <= 7 * DAY;
  if (filter === "30d") return age <= 30 * DAY;
  if (filter === "inactive30d") return age > 30 * DAY;
  return true;
}

export function filterAndSortAdminUsers(users = [], options = {}, now = Date.now()) {
  const {
    query = "",
    platform = "all",
    delivery = "all",
    language = "all",
    version = "all",
    activity = "all",
    premium = "all",
    sort = "newest",
  } = options;
  const normalizedQuery = String(query).trim().toLocaleLowerCase("tr-TR");

  const filtered = users.filter((user) => {
    const searchable = [userName(user), user?.email, user?.kullaniciAdi, user?.telefon]
      .filter(Boolean)
      .join(" ")
      .toLocaleLowerCase("tr-TR");
    if (normalizedQuery && !searchable.includes(normalizedQuery)) return false;
    const platforms = adminUserPlatforms(user);
    if (platform === "multi" && !(platforms.includes("ios") && platforms.includes("android"))) return false;
    if (!["all", "multi"].includes(platform) && !platforms.includes(platform)) return false;
    if (delivery === "push" && !adminUserPushReady(user)) return false;
    if (delivery === "in_app" && adminUserPushReady(user)) return false;
    if (language !== "all" && userLanguage(user) !== language) return false;
    if (version !== "all" && String(user?.appVersion || "") !== version) return false;
    if (premium === "premium" && !adminUserIsPremium(user)) return false;
    if (premium === "free" && adminUserIsPremium(user)) return false;
    return matchesActivity(user, activity, now);
  });

  const compareText = (a, b) => trCollator.compare(a, b);
  const comparisons = {
    newest: (a, b) => parseDate(b.createdAt) - parseDate(a.createdAt),
    oldest: (a, b) => parseDate(a.createdAt) - parseDate(b.createdAt),
    nameAsc: (a, b) => compareText(userName(a), userName(b)),
    nameDesc: (a, b) => compareText(userName(b), userName(a)),
    emailAsc: (a, b) => compareText(String(a.email || ""), String(b.email || "")),
    emailDesc: (a, b) => compareText(String(b.email || ""), String(a.email || "")),
    platform: (a, b) => compareText(adminUserPlatformLabel(a), adminUserPlatformLabel(b)),
    push: (a, b) => Number(adminUserPushReady(b)) - Number(adminUserPushReady(a)),
    language: (a, b) => compareText(userLanguage(a), userLanguage(b)),
    versionDesc: (a, b) => compareAdminVersions(b.appVersion, a.appVersion),
    versionAsc: (a, b) => compareAdminVersions(a.appVersion, b.appVersion),
    activeDesc: (a, b) => lastActiveMs(b) - lastActiveMs(a),
    activeAsc: (a, b) => (lastActiveMs(a) || Number.MAX_SAFE_INTEGER) - (lastActiveMs(b) || Number.MAX_SAFE_INTEGER),
  };
  const selectedComparison = comparisons[sort] || comparisons.newest;

  return filtered.map((user, index) => ({ user, index })).sort((left, right) => (
    selectedComparison(left.user, right.user)
      || parseDate(right.user.createdAt) - parseDate(left.user.createdAt)
      || compareText(String(left.user.uid || left.user.id || left.index), String(right.user.uid || right.user.id || right.index))
  )).map(({ user }) => user);
}

export function adminUserVersionOptions(users = []) {
  return Array.from(new Set(users.map((user) => String(user?.appVersion || "").trim()).filter(Boolean)))
    .sort((a, b) => compareAdminVersions(b, a));
}

export function formatAdminLastActive(value, lang = "tr", now = Date.now()) {
  const activeAt = parseDate(value);
  if (!activeAt) return "—";
  const age = Math.max(0, now - activeAt);
  if (age < MINUTE) return lang === "en" ? "Now" : "Şimdi";
  if (age < HOUR) return `${Math.floor(age / MINUTE)} ${lang === "en" ? "min" : "dk"}`;
  if (age < DAY) return `${Math.floor(age / HOUR)} ${lang === "en" ? "h" : "sa"}`;
  if (age < 30 * DAY) return `${Math.floor(age / DAY)} ${lang === "en" ? "d" : "g"}`;
  return new Date(activeAt).toLocaleDateString(lang === "en" ? "en-US" : "tr-TR", {
    day: "2-digit",
    month: "short",
  });
}

export function formatAdminExactActivity(value, lang = "tr") {
  const activeAt = parseDate(value);
  if (!activeAt) return lang === "en" ? "No activity recorded" : "Aktivite kaydı yok";
  return new Date(activeAt).toLocaleString(lang === "en" ? "en-US" : "tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
